import { FC, useState } from 'react';
import { BannerType } from '@weave/schema-gen-ts/dist/schemas/forms-digital/forms_messages.pb';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { DigitalFormsQueries, DigitalFormsMutations } from '@frontend/api-digital-forms';
import { getUser } from '@frontend/auth-helpers';
import { GlobalBannerDynamic } from '@frontend/global-info-banner';
import { useTranslation } from '@frontend/i18n';
import { useAlert } from '@frontend/design-system';
import { useDigitalFormsFreemiumContext } from './context';
import { useFormsFreemiumFeatureFlag } from './useFormsFreemiumFeatureFlag';

interface FreemiumUpgradeAlertProps {
  feature: Feature;
}

export const FreemiumUpgradeAlert: FC<FreemiumUpgradeAlertProps> = ({ feature }) => {
  const { t } = useTranslation('forms');
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const { isFreemium, freemiumLocationIds } = useFormsFreemiumFeatureFlag();
  const { featureUpgradeDetailsModalControls } = useDigitalFormsFreemiumContext();

  const alert = useAlert();

  const user = getUser();

  const companyId = freemiumLocationIds[0];
  const userId = user?.userID;

  const { data: bannerData, isLoading } = DigitalFormsQueries.useGetBannerStatus(
    {
      companyId: companyId || '',
      userId: userId || '',
      bannerType: BannerType.BANNER_TYPE_FREEMIUM,
    },
    {
      enabled: isFreemium && !!companyId && !!userId,
    }
  );

  const { mutateAsync } = DigitalFormsMutations.useDismissBanner({});

  const formCount = bannerData?.formsCount;
  const shouldShow = bannerData?.shouldShow;

  if (!shouldShow || !isFreemium || bannerDismissed || isLoading || !companyId || !userId) {
    return null;
  }

  const onDismissBanner = async () => {
    try {
      await mutateAsync({
        companyId,
        userId,
        bannerType: BannerType.BANNER_TYPE_FREEMIUM,
      });
      setBannerDismissed(true);
    } catch (error) {
      alert.error(t('Error while dismissing the banner'));
    }
  };

  return (
    <GlobalBannerDynamic
      type='info'
      id={`${feature}-freemium-upgrade-banner`}
      title={t('You have sent over {{count}} forms so far using forms freemium.', {
        count: formCount,
      })}
      isDismissible
      onDismiss={onDismissBanner}
      message={t(
        'Weave Forms can sync directly with your source system. Upgrade to the Elite Bundle to automatically sync your forms into it.'
      )}
      trackingId='forms-freemium-upgrade-banner-clicked'
      action={{
        label: t('Upgrade Locations'),
        onClick: () => featureUpgradeDetailsModalControls.openModal(),
      }}
    />
  );
};
